<script setup lang="ts">
import type { CmsElementProductListing } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import SwProductCard from "../SwProductCard.vue";
import { useListing } from "#imports";
import { computed } from "vue";
import { defu } from "defu";
import { useRoute, useRouter } from "vue-router";
import ProductListingSteps from "./ProductListingSteps.vue";
import {getProductList} from "../../../../server/apiBuilder";
import {productListTracking} from "../../utilities/GtmTrackings";

const props = defineProps<{
  content: CmsElementProductListing;
  class: any;
  style: any;
}>();

type Translations = {
  listing: {
    noProducts: string;
  };
};
let translations: Translations = {
  listing: {
    noProducts: "No products found 😔",
  },
};
translations = defu(translations, useCmsTranslations()) as Translations;

const defaultLimit = 24;
const categoryId = props.content.data.listing.currentFilters.navigationId;

const {
  getElements,
  setInitialListing,
  getCurrentPage,
  getTotalPagesCount,
  getCurrentFilters,
  getTotal,
  loading,
} = useListing({ listingType: "categoryListing", categoryId: "" });

const route = useRoute();
const router = useRouter();
const isLoading = ref(false);

const loadMore = async (query: Object = {}) => {
  isLoading.value = true;
  const productQuery = Object.keys(query).length > 0 ? query.page : null;

  if (!loading.value && getTotalPagesCount.value > 1 && getCurrentPage.value !== getTotalPagesCount.value) {
    const properties = getCurrentFilters.value?.properties.length > 0 ? getCurrentFilters.value.properties : "";
    const page = Object.keys(route.query).length > 0 && route.query.page ? parseInt(route.query.page) + 1 : 2;

    const body = {
      limit: defaultLimit,
      p: productQuery ? productQuery : page,
      properties: properties,
      search: ""
    };

    try {
      const currentProducts = await getProductList(categoryId, body);
      if (currentProducts && Array.isArray(currentProducts.elements)) {
        productData.value.push(...currentProducts.elements); // Append new products to the existing list
        productListTracking(productData.value);
      } else {
        throw new Error('Failed to fetch products or data format is incorrect.');
      }

      await router.push({
        query: {
          page: productQuery ? productQuery : page,
        },
      });

    } catch (error) {
      console.error('Failed to load more products:', error);
    }
  }
  isLoading.value = false;
};

const loadInitalPagesFromRequest = async (query: Object = {}) => {
  isLoading.value = true;
  if (!loading.value && getTotalPagesCount.value > 1 && getCurrentPage.value !== getTotalPagesCount.value) {
    const properties = getCurrentFilters.value?.properties.length > 0 ? getCurrentFilters.value.properties : "";
    const page = Object.keys(route.query).length > 0 && route.query.page ? parseInt(route.query.page) + 1 : 2;

    try {
      for (let i = 2; i < page; i++) {
        const body = {
          limit: defaultLimit,
          p: i,
          properties: properties,
          search: ""
        };

        const currentProducts = await getProductList(categoryId, body);
        if (currentProducts && Array.isArray(currentProducts.elements)) {
          productData.value.push(...currentProducts.elements); // Append new products to the existing list
        } else {
          throw new Error('Failed to fetch products or data format is incorrect.');
        }
      }
    } catch (error) {
      console.error('Failed to load more products:', error);
    }
  }
  isLoading.value = false;
};


const isProductListing = computed(
    () => props.content?.type === "product-listing"
);

setInitialListing(props?.content?.data?.listing);

let productData: any = computed (() => (getElements.value));



onMounted( async ()=> {
  if (Object.keys(route.query).length > 0) {
    await loadInitalPagesFromRequest(route?.query)
  }

  productListTracking(productData.value);
});

</script>

<template>
  <div class="section--body"  :style="props.style" :class="props.class">
    <div class="container">
      <div class="row">
        <div class="col-lg-10">
          <div class="row">
            <SwProductCard
                v-for="product in productData"
                :key="product.id"
                :product="product"
                :isProductListing="isProductListing"
                class="p-4"
            />
            <div class="my-5 ">

              <div class="load-more-info d-flex justify-content-center fw-bold mb-4">
                 <span>{{productData.length }}&nbsp;</span> {{$t('products.loaded.shown.from')}} <span>&nbsp;{{getTotal}}&nbsp;</span> {{$t('products.loaded.shown')}}
              </div>
              <div>

              </div>
              <div :class="{'d-none' : productData.length == getTotal}" class="load-more d-flex justify-content-center">
                <button @click="loadMore()" :disabled="isLoading" class="btn btn-default btn-danger">
                  <span v-if="isLoading">{{$t('products.load.more.isLoading')}}</span>
                  <span v-else>{{$t('products.load.more')}}</span>
                </button>
              </div>

            </div>
          </div>
        </div>
        <div class="col-lg-2 order-lg-2">
          <ProductListingSteps />
        </div>
        <div v-if="!getElements?.length" class="col-lg-9 order-lg-1">
          <h2 class="mx-auto text-center">
            {{ translations.listing.noProducts }}
          </h2>
        </div>
      </div>
    </div>
  </div>
</template>
